'use client'

import { useEffect, useRef } from 'react'
import Link from 'next/link'
import { PRODUCTS } from './navData'

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
  navItems: ReadonlyArray<{ label: string; href: string }>
  activeHref: string
  ctaHref: string
  ctaLabel: string
}

export function MobileMenu({ isOpen, onClose, navItems, activeHref, ctaHref, ctaLabel }: MobileMenuProps) {
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const overlay = overlayRef.current
    if (!overlay) return
    const focusables = Array.from(
      overlay.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
    )
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab') return
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus() }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }
    document.addEventListener('keydown', onKey)
    first?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  return (
    <div
      ref={overlayRef}
      id="mobile-menu"
      className={`lg:hidden fixed inset-0 z-60 transition-all duration-300 ${
        isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
      }`}
      aria-hidden={!isOpen}
    >
      <div className="absolute inset-0 bg-deep" />

      <div className="relative z-10 h-full max-h-dvh overflow-y-auto overscroll-contain pt-[calc(env(safe-area-inset-top)+4rem)] pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
        <div className="min-h-full flex flex-col justify-center px-6">
          <div className="mb-6 pb-6 border-b border-white/10">
            <p className="font-montserrat-alt text-xs text-white/70 uppercase tracking-widest mb-3 px-4">
              Продукти
            </p>
            <div className="space-y-1">
              {PRODUCTS.map((product, index) => (
                <Link
                  key={product.href}
                  href={product.href}
                  onClick={onClose}
                  className={`flex flex-col min-h-[60px] py-3 px-4 rounded-2xl font-montserrat-alt touch-manipulation transition-all duration-300 ${
                    activeHref === product.href
                      ? 'bg-white/10 text-rose'
                      : 'text-white hover:text-rose hover:bg-white/5 active:bg-white/10'
                  } ${
                    isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                  }`}
                  style={{ transitionDelay: isOpen ? `${index * 25 + 20}ms` : '0ms' }}
                >
                  <span className="font-alumni text-xl font-bold leading-tight">{product.label}</span>
                  <span className="text-sm text-white/70 mt-0.5">{product.description}</span>
                </Link>
              ))}
              <Link
                href="/products"
                onClick={onClose}
                className={`flex items-center justify-between min-h-[48px] py-2.5 px-4 mt-1 rounded-2xl font-montserrat-alt text-sm text-white/70 hover:text-white hover:bg-white/5 active:bg-white/10 touch-manipulation transition-all duration-300 ${
                  isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                }`}
                style={{ transitionDelay: isOpen ? `${PRODUCTS.length * 25 + 20}ms` : '0ms' }}
              >
                <span>Усі продукти</span>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
                </svg>
              </Link>
            </div>
          </div>

          <div className="space-y-1">
            {navItems.map((item, index) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={onClose}
                className={`flex items-center min-h-[60px] py-4 px-4 rounded-2xl font-montserrat-alt text-[22px] text-white hover:text-rose hover:bg-white/5 active:bg-white/10 touch-manipulation transition-all duration-300 ${
                  isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
                }`}
                style={{ transitionDelay: isOpen ? `${index * 25 + 80}ms` : '0ms' }}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="mt-8 px-4">
            <a
              href={ctaHref}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              className={`flex items-center justify-center gap-2 w-full font-montserrat-alt text-base font-semibold min-h-[56px] px-6 py-4 rounded-full bg-rose text-deep hover:bg-cream active:scale-[0.98] touch-manipulation transition-all duration-300 ${
                isOpen ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
              }`}
              style={{ transitionDelay: isOpen ? `${navItems.length * 25 + 120}ms` : '0ms' }}
            >
              {ctaLabel}
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m0 0H8.25m11.25 0v11.25" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  )
}
